/* R148c · Feb 2026 — Reusable dialog that asks the user for the actual
 * clock-out time of a stale open punch. Uses a native datetime-local
 * input (much friendlier than window.prompt) and validates client-side. */
import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertTriangle } from "lucide-react";

function toLocalInputValue(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  // format as YYYY-MM-DDTHH:MM in the user's browser tz
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function StalePunchDialog({ openPunch, onConfirm, onCancel }) {
  const cinLocal = useMemo(() => toLocalInputValue(openPunch?.clock_in), [openPunch]);
  const nowLocal = useMemo(() => toLocalInputValue(new Date().toISOString()), []);
  const [val, setVal] = useState(nowLocal);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setErr("");
    if (!val) { setErr("Pick a clock-out time"); return; }
    const cout = new Date(val);
    const cin = new Date(openPunch.clock_in);
    if (isNaN(cout.getTime())) { setErr("Invalid date"); return; }
    if (cout <= cin) { setErr("Clock-out time must be after clock-in"); return; }
    setBusy(true);
    try {
      await onConfirm(cout.toISOString());
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v && !busy) onCancel(); }}>
      <DialogContent className="max-w-md" data-testid="stale-punch-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            You were still clocked in
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            An earlier session is still open from <b>{new Date(openPunch.clock_in).toLocaleString()}</b>.
            Enter your actual clock-out time so we close it correctly before starting a new one.
          </p>
          <div>
            <div className="label-eyebrow text-[10px] mb-1">Clock-out time (your local time)</div>
            <Input
              type="datetime-local"
              value={val}
              min={cinLocal}
              max={nowLocal}
              onChange={(e) => setVal(e.target.value)}
              className="h-9"
              data-testid="stale-punch-cout-input"
            />
            <p className="text-[10px] text-muted-foreground mt-1">
              Sessions longer than 12 hours are auto-capped by the server to <b>{new Date(new Date(openPunch.clock_in).getTime() + 12 * 3600 * 1000).toLocaleString()}</b>.
            </p>
          </div>
          {err && <div className="text-xs text-red-600" data-testid="stale-punch-err">{err}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onCancel} disabled={busy} data-testid="stale-punch-cancel">Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="stale-punch-submit">
            {busy ? "Closing…" : "Close session & continue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
